import type { GlobalNaviDataV2 } from '@rt/global-navi/contract';
import { Env } from 'auth-common/shared';
import { onAuthStateChanged } from 'firebase/auth';
import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';
import { firebaseAuth } from '../firebaseConfig';
import { useAuth } from '../hook/Api/Auth';
import type { AuthInfo } from '../hook/model/authInfo';
import { useLatestProjectId } from '../hook/Project/latestProject';
import { hasSessionCookieAuthCredential } from '../utils';
import { isAuthRequirementError } from '../utils/authRequirements';

export type AuthState = AuthState.Initial | AuthState.Authenticated | AuthState.UnAuthenticated;
export namespace AuthState {
  export type Initial = { type: 'initial' };
  /** globalNavi は bootstrap-v2 経路で取得できた場合のみ含まれる。initial auth fallback では undefined。 */
  export type Authenticated = { type: 'authenticated'; authInfo: AuthInfo; globalNavi?: GlobalNaviDataV2 };
  export type UnAuthenticated = { type: 'unAuthenticated' };

  export const Create = (params: {
    authInfo: AuthInfo | undefined;
    globalNavi: GlobalNaviDataV2 | undefined;
    isPending: boolean;
    firebaseAuthState: FirebaseAuthState;
    hasAuthCredential: boolean;
  }): AuthState => {
    const { authInfo, globalNavi, isPending, firebaseAuthState, hasAuthCredential } = params;
    if (!hasAuthCredential && firebaseAuthState.type === 'initial') return { type: 'initial' };
    if (!hasAuthCredential) return { type: 'unAuthenticated' };
    if (firebaseAuthState.type === 'initial' && isPending) return { type: 'initial' };

    // 初回のデータ取得前かどうか (firebaseAuth が unAuthenticated の場合も初回取得を行わないため true となることに注意)
    if (isPending) return { type: 'initial' };
    // firebaseAuth が authenticated の場合も account が取得できなかった場合は unAuthenticated として扱う
    if (!authInfo) return { type: 'unAuthenticated' };
    return { type: 'authenticated', authInfo, ...(globalNavi ? { globalNavi } : {}) };
  };

  export const GetAuthInfo = (state: AuthState): AuthInfo | undefined => {
    if (state.type === 'authenticated') return state.authInfo;
    return undefined;
  };

  export const GetGlobalNavi = (state: AuthState): GlobalNaviDataV2 | undefined => {
    if (state.type === 'authenticated') return state.globalNavi;
    return undefined;
  };

  export namespace Is {
    export const Initial = (state: AuthState): state is Initial => state.type === 'initial';
    export const Authenticated = (state: AuthState): state is Authenticated => state.type === 'authenticated';
    export const UnAuthenticated = (state: AuthState): state is UnAuthenticated => state.type === 'unAuthenticated';
  }
}

type FirebaseAuthState = { type: 'initial' } | { type: 'authenticated'; uid: string } | { type: 'unAuthenticated' };
namespace FirebaseAuthState {
  export const GetUid = (state: FirebaseAuthState): string | undefined => {
    if (state.type === 'authenticated') return state.uid;
    return undefined;
  };
}

export const AuthContext = createContext<{ authState: AuthState }>({ authState: { type: 'initial' } });

export const useAuthContext = () => useContext(AuthContext);

export namespace AuthProvider {
  export type Props = {
    /** 表示中 project の明示指定。省略時は URL の `?pid=` から解決する。 */
    initialProjectId?: string | undefined;
    /** project bootstrap を使わず、アカウント・組織単位の認証情報だけを取得する。 */
    skipProjectBootstrap?: boolean;
  };
}

// project の切替は全プロダクトでフルページ遷移 (window.location.href) なので、
// pid はページロード時に一度読めば十分。
const getProjectIdFromUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('pid') ?? undefined;
};

export const AuthProvider: React.FC<PropsWithChildren<AuthProvider.Props>> = ({
  initialProjectId,
  skipProjectBootstrap = false,
  children,
}) => {
  const [firebaseAuthState, setFirebaseAuthState] = useState<FirebaseAuthState>({ type: 'initial' });
  const uid = FirebaseAuthState.GetUid(firebaseAuthState);
  const hasSessionCredential = hasSessionCookieAuthCredential();
  const authUid = skipProjectBootstrap && hasSessionCredential ? undefined : uid;

  // 表示中の project (URL の `?pid=`) を基準に get-bootstrap-v2 を叩く。
  // localStorage の latestProjectId は URL に pid が無い画面 (プロジェクト選択前など) 向けのフォールバック。
  // 以下のケースでは bootstrap を叩かず initial auth info にフォールバックする:
  //  - projectId がどこからも解決できない (初回ログイン直後、project 選択前)
  //  - bootstrap がエラーを返した (所属外 project / 一時障害)
  // MFA/SSO 403 はフォールバックせず Redirector へ委ねる (get-initial-auth-info は project スコープ未チェックのため)。
  const [urlProjectId] = useState(getProjectIdFromUrl);
  const { latestProjectId } = useLatestProjectId();
  const projectId = skipProjectBootstrap ? undefined : (initialProjectId ?? urlProjectId ?? latestProjectId);
  const hasAuthCredential = authUid !== undefined || hasSessionCredential;
  const wantBootstrap = Boolean(hasAuthCredential && projectId);

  const bootstrap = useAuth.GetBootstrap({
    uid: wantBootstrap ? authUid : undefined,
    projectId: wantBootstrap ? projectId : undefined,
    enabled: wantBootstrap,
  });
  const bootstrapAuthRequirementError = bootstrap.error !== undefined && isAuthRequirementError(bootstrap.error);
  const needFallback = !wantBootstrap || (bootstrap.isError && !bootstrapAuthRequirementError);
  const fallback = useAuth.GetInitialAuthInfo({
    uid: needFallback && hasAuthCredential ? authUid : undefined,
    enabled: needFallback && hasAuthCredential,
  });

  const authInfo = bootstrapAuthRequirementError ? undefined : (bootstrap.authInfo ?? fallback.authInfo);
  const globalNavi = bootstrapAuthRequirementError ? undefined : bootstrap.globalNavi;
  const isAuthSourcePending = skipProjectBootstrap && firebaseAuthState.type === 'initial';
  const isPending = isAuthSourcePending || (bootstrapAuthRequirementError ? true : needFallback ? fallback.isPending : bootstrap.isPending);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      setFirebaseAuthState(firebaseUser ? { type: 'authenticated', uid: firebaseUser.uid } : { type: 'unAuthenticated' });
    });
    const firebaseUser = firebaseAuth.currentUser;
    if (firebaseUser) {
      setFirebaseAuthState({ type: 'authenticated', uid: firebaseUser.uid });
      if (!Env.IsProd) {
        console.log('@@@ firebaseAuth.currentUser', { firebaseUser });
      }
    }

    return unsubscribe;
  }, []);

  // initial のまま状態が変わらない場合は currentUser を参照して設定する
  const isInitial = firebaseAuthState.type === 'initial';
  useEffect(() => {
    if (!isInitial) return;

    const fallbackTimer = setTimeout(() => {
      const firebaseUser = firebaseAuth.currentUser;
      console.error('@@@ onAuthStateChanged が呼び出されていません', { firebaseUser });
      setFirebaseAuthState(firebaseUser ? { type: 'authenticated', uid: firebaseUser.uid } : { type: 'unAuthenticated' });
    }, 10 * 1000); // 10秒後に確認

    return () => clearTimeout(fallbackTimer);
  }, [isInitial]);

  const authState = AuthState.Create({ authInfo, globalNavi, isPending, firebaseAuthState, hasAuthCredential });
  return <AuthContext.Provider value={{ authState }}>{children}</AuthContext.Provider>;
};
